import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useConnectorIdentity } from '~/hooks/auth/useConnectorIdentity';
import { useClinicalListsListPeople } from '~/backend/connector-next-api/connector-next-components';
import { useToday } from '~/hooks/useToday';
import { useClinicalLists, type TaskList } from '../hooks';
import {
    listTableColumnOf,
    STAGE_STRIP_MIN_TRACK,
    type ListTableColumn,
} from '../PatientList/PatientListTable';
import { ListToolbarFrame, PatientTablePage } from '../shared';
import TaskRow from './TaskRow';

/** The patient list's columns, then the actions. */
const COLUMNS: readonly ListTableColumn[] = [
    listTableColumnOf('patient'),
    listTableColumnOf('care'),
    listTableColumnOf('treatmentStart'),
    listTableColumnOf('workflow'),
    { track: 158, alignEnd: true },
];

type TaskListViewProps = {
    list: TaskList;
    /** The list's views, beside the title. */
    tabs: ReactNode;
};

/**
 * The patients assigned to the signed-in user, each with where its workflow
 * stands and releasing it. A patient is assigned to at most one person.
 */
export default function TaskListView({ list, tabs }: TaskListViewProps) {
    const intl = useIntl();
    const today = useToday();
    const canAssign = useConnectorIdentity().canAssignPatients;
    const { data: lists } = useClinicalLists();
    const { data: people } = useClinicalListsListPeople({});

    return (
        <PatientTablePage
            toolbar={
                <ListToolbarFrame tabs={tabs} isFetching={list.isRefreshing} />
            }
            columns={COLUMNS}
            flexMinTrack={STAGE_STRIP_MIN_TRACK}
            rows={list.patients.map((patient) => (
                <TaskRow
                    key={patient.id}
                    patient={patient}
                    lists={lists}
                    people={people}
                    canAssign={canAssign}
                    today={today}
                />
            ))}
            message={
                !list.isLoading && !list.isError && list.patients.length === 0
                    ? intl.formatMessage({ id: 'patientList.tasks.mineEmpty' })
                    : undefined
            }
            isLoading={list.isLoading}
            isError={list.isError}
        />
    );
}
